import { useState, useMemo } from 'react';
import { Funcionario } from '@/context/FuncionariosContext';
import { calcularProgressao } from '@/utils/progressaoUtils';
import FuncionarioRow from './FuncionarioRow';

interface FuncionariosTableProps {
  funcionarios: Funcionario[];
  pontosPorFuncionario: Record<string, number>;
  limiarPorFuncionario?: Record<string, number>;
  regraEspecialPorFuncionario?: Record<string, boolean>;
  periodoPorFuncionario?: Record<string, string>;
  prepavPorFuncionario?: Record<string, boolean>;
  onRegistarProgressao?: (f: Funcionario) => void;
}

type SortKey = 'id' | 'nome' | 'carreira' | 'sexo' | 'ultimaProgressao' | 'novoNivel' | 'pontos' | 'elegibilidade' | null;
type SortDir = 'asc' | 'desc';

const ELEGIBILIDADE_ORDER: Record<string, number> = { pronto: 0, proximo: 1, em_progresso: 2 };

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const headers: { label: string; icon: string; key: SortKey }[] = [
  { label: 'Nº Funcionário', icon: 'ri-id-card-line', key: 'id' },
  { label: 'Nome', icon: 'ri-user-line', key: 'nome' },
  { label: 'Carreira', icon: 'ri-briefcase-line', key: 'carreira' },
  { label: 'Sexo', icon: 'ri-group-line', key: 'sexo' },
  { label: 'Última Progressão', icon: 'ri-calendar-check-line', key: 'ultimaProgressao' },
  { label: 'Novo Nível 2025', icon: 'ri-arrow-up-circle-line', key: 'novoNivel' },
  { label: 'Pontos', icon: 'ri-bar-chart-line', key: 'pontos' },
  { label: 'Elegibilidade', icon: 'ri-checkbox-circle-line', key: 'elegibilidade' },
  { label: '', icon: '', key: null },
];

export default function FuncionariosTable({
  funcionarios,
  pontosPorFuncionario,
  limiarPorFuncionario,
  regraEspecialPorFuncionario,
  periodoPorFuncionario,
  prepavPorFuncionario,
  onRegistarProgressao,
}: FuncionariosTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function handleSort(key: SortKey) {
    if (!key) return;
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  const sorted = useMemo(() => {
    return [...funcionarios].sort((a, b) => {
      let valA: string | number = 0;
      let valB: string | number = 0;

      switch (sortKey) {
        case 'id':
          valA = parseInt(a.id, 10) || 0;
          valB = parseInt(b.id, 10) || 0;
          break;
        case 'nome':
          valA = a.nome.toLowerCase();
          valB = b.nome.toLowerCase();
          break;
        case 'carreira':
          valA = a.carreira.toLowerCase();
          valB = b.carreira.toLowerCase();
          break;
        case 'sexo':
          valA = a.sexo;
          valB = b.sexo;
          break;
        case 'ultimaProgressao':
          valA = a.ultimaProgressao || '';
          valB = b.ultimaProgressao || '';
          break;
        case 'novoNivel':
          valA = a.novoNivel2025 ?? 0;
          valB = b.novoNivel2025 ?? 0;
          break;
        case 'pontos':
          valA = pontosPorFuncionario[a.id] ?? 0;
          valB = pontosPorFuncionario[b.id] ?? 0;
          break;
        case 'elegibilidade': {
          const limiarA = limiarPorFuncionario?.[a.id] ?? 8;
          const limiarB = limiarPorFuncionario?.[b.id] ?? 8;
          const progA = calcularProgressao(pontosPorFuncionario[a.id] ?? 0, a.nivelAtual, limiarA);
          const progB = calcularProgressao(pontosPorFuncionario[b.id] ?? 0, b.nivelAtual, limiarB);
          valA = ELEGIBILIDADE_ORDER[progA.status] ?? 99;
          valB = ELEGIBILIDADE_ORDER[progB.status] ?? 99;
          break;
        }
        default:
          return 0;
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [funcionarios, sortKey, sortDir, pontosPorFuncionario, limiarPorFuncionario]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  // Reset to page 1 when list changes
  useMemo(() => { setPage(1); }, [funcionarios.length]);

  if (funcionarios.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 py-20 flex flex-col items-center gap-3 text-slate-400">
        <div className="w-12 h-12 flex items-center justify-center">
          <i className="ri-search-line text-4xl" />
        </div>
        <p className="text-sm">Nenhum funcionário encontrado com os filtros aplicados.</p>
      </div>
    );
  }

  const startItem = (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, sorted.length);

  function getPageNumbers(): (number | '...')[] {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (safePage > 3) pages.push('...');
    for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) {
      pages.push(i);
    }
    if (safePage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  }

  return (
    <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/60">
              {headers.map((h, i) => (
                <th
                  key={i}
                  onClick={() => handleSort(h.key)}
                  className={`px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap select-none group ${
                    h.key
                      ? 'cursor-pointer hover:bg-zinc-100 transition-colors'
                      : ''
                  } ${sortKey === h.key ? 'text-zinc-700' : 'text-zinc-400'}`}
                >
                  {h.icon && (
                    <span className="inline-flex items-center gap-1.5">
                      <i className={`${h.icon} text-xs`} />
                      {h.label}
                      {h.key && (
                        <span className="inline-flex items-center ml-0.5">
                          {sortKey === h.key ? (
                            <i
                              className={`${
                                sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'
                              } text-[14px] text-zinc-700`}
                            />
                          ) : (
                            <i className="ri-expand-up-down-line text-[11px] text-zinc-300 group-hover:text-zinc-400" />
                          )}
                        </span>
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((f, i) => (
              <FuncionarioRow
                key={f.id}
                funcionario={f}
                index={(safePage - 1) * pageSize + i}
                pontosAtuais={pontosPorFuncionario[f.id] ?? f.pontosAtuais ?? 0}
                limiar={limiarPorFuncionario?.[f.id]}
                regraEspecial18Anos={regraEspecialPorFuncionario?.[f.id] ?? false}
                periodoEscala={periodoPorFuncionario?.[f.id]}
                isPrepav={prepavPorFuncionario?.[f.id] ?? false}
                onRegistarProgressao={onRegistarProgressao}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer com paginação */}
      <div className="px-5 py-3 border-t border-zinc-100 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <p className="text-xs text-slate-400">
            A mostrar <span className="font-semibold text-slate-600">{startItem}–{endItem}</span> de{' '}
            <span className="font-semibold text-slate-600">{sorted.length}</span> funcionário(s)
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400">Por página:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-300 cursor-pointer"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={safePage === 1}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Primeira página"
            >
              <i className="ri-skip-back-line text-sm" />
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Página anterior"
            >
              <i className="ri-arrow-left-s-line text-sm" />
            </button>

            {getPageNumbers().map((p, idx) =>
              p === '...' ? (
                <span key={`ellipsis-${idx}`} className="w-7 h-7 flex items-center justify-center text-xs text-slate-400">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p as number)}
                  className={`whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-md text-xs font-medium transition-colors cursor-pointer ${
                    safePage === p
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                  }`}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Próxima página"
            >
              <i className="ri-arrow-right-s-line text-sm" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage === totalPages}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Última página"
            >
              <i className="ri-skip-forward-line text-sm" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
